// src/pages/epl/innovatech/TeamSelector.jsx
// 👥 InnovaTech - Selector y gestión de equipos
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Copy, 
  Check, 
  UserPlus, 
  Crown, 
  ArrowLeft,
  Home,
  Search,
  Settings
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const TeamSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('join'); // 'join' | 'create' | 'manage'
  const [teamCode, setTeamCode] = useState('');
  const [teamName, setTeamName] = useState('');
  const [myTeam, setMyTeam] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    // Simular carga de equipos disponibles (en producción vendría del backend)
    const mockTeams = [
      {
        id: 'team_001',
        name: 'Tech Innovators',
        leader: { name: 'María García', id: 'user_456' },
        members: [
          { name: 'María García', id: 'user_456', role: 'leader' },
          { name: 'Carlos López', id: 'user_457', role: 'member' },
          { name: 'Ana Rodríguez', id: 'user_458', role: 'member' }
        ],
        code: 'TECH2024',
        created_at: '2024-11-01',
        labs_progress: {
          lab1: 100,
          lab2: 75, 
          lab3: 0,
          lab4: 0,
          lab5: 0
        }
      },
      {
        id: 'team_002', 
        name: 'StartupLab Heroes',
        leader: { name: 'Diego Morales', id: 'user_459' },
        members: [
          { name: 'Diego Morales', id: 'user_459', role: 'leader' },
          { name: 'Sofia Chen', id: 'user_460', role: 'member' }
        ],
        code: 'HERO2024',
        created_at: '2024-11-02',
        labs_progress: {
          lab1: 100,
          lab2: 100,
          lab3: 30,
          lab4: 0,
          lab5: 0
        }
      }
    ];
    
    setAvailableTeams(mockTeams);
    
    // Verificar si el usuario ya pertenece a un equipo
    const userTeam = mockTeams.find(team => 
      team.members.some(member => member.id === user?.id)
    );
    
    if (userTeam) {
      setMyTeam(userTeam);
      setActiveTab('manage');
    }
  }, [user]);

  const handleJoinTeam = async (code) => {
    setLoading(true);
    
    // Simular llamada a API
    setTimeout(() => {
      const team = availableTeams.find(t => t.code === code);
      
      if (team) {
        // Agregar usuario al equipo
        const updatedTeam = {
          ...team,
          members: [...team.members, { name: user.name, id: user.id, role: 'member' }]
        };
        
        setMyTeam(updatedTeam);
        setActiveTab('manage');
        setTeamCode('');
      } else {
        alert('Código de equipo no válido');
      }
      
      setLoading(false);
    }, 1500);
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      alert('Por favor ingresa un nombre para el equipo');
      return;
    }
    
    setLoading(true);
    
    // Simular creación de equipo
    setTimeout(() => {
      const newTeam = {
        id: `team_${Date.now()}`,
        name: teamName,
        leader: { name: user.name, id: user.id },
        members: [{ name: user.name, id: user.id, role: 'leader' }],
        code: generateTeamCode(),
        created_at: new Date().toISOString().split('T')[0],
        labs_progress: {
          lab1: 0,
          lab2: 0,
          lab3: 0,
          lab4: 0,
          lab5: 0
        }
      };
      
      setMyTeam(newTeam);
      setActiveTab('manage');
      setTeamName('');
      setLoading(false);
    }, 1500);
  };

  const generateTeamCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const copyTeamCode = () => {
    if (myTeam?.code) {
      navigator.clipboard.writeText(myTeam.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleStartLabs = () => {
    navigate('/dashboard/innova-tech/lab/lab1');
  };

  const handleGoHome = () => {
    navigate('/dashboard/innova-tech');
  };

  const calculateOverallProgress = (labsProgress) => {
    const values = Object.values(labsProgress);
    return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleGoHome}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <Home className="w-4 h-4" />
          <span>Volver</span>
        </button>
        
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Equipos</h1>
        
        <div className="w-20" /> {/* Spacer */}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('join')}
            className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
              activeTab === 'join' 
                ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserPlus className="w-5 h-5 mx-auto mb-1" />
            Unirse a Equipo
          </button>
          
          <button
            onClick={() => setActiveTab('create')}
            className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
              activeTab === 'create'
                ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Plus className="w-5 h-5 mx-auto mb-1" />
            Crear Equipo
          </button>
          
          {myTeam && (
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex-1 px-4 py-3 text-center font-medium transition-colors ${
                activeTab === 'manage'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="w-5 h-5 mx-auto mb-1" />
              Mi Equipo
            </button>
          )}
        </div>

        <div className="p-6">
          {/* Join Team Tab */}
          {activeTab === 'join' && (
            <div className="space-y-6">
              <div className="text-center">
                <Users className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Unirse a un Equipo</h2>
                <p className="text-gray-600">
                  Ingresa el código que te compartió el líder de tu equipo
                </p>
              </div>
              
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código del Equipo
                  </label>
                  <input
                    type="text"
                    value={teamCode}
                    onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
                    placeholder="Ej: TECH2024"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
                    maxLength={8}
                  />
                </div>
                
                <button
                  onClick={() => handleJoinTeam(teamCode)}
                  disabled={!teamCode.trim() || loading}
                  className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'Uniéndose...' : 'Unirse al Equipo'}
                </button>
              </div>

              {/* Available Teams Preview */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Equipos Disponibles</h3>
                <div className="grid gap-4">
                  {availableTeams.map((team) => (
                    <div key={team.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{team.name}</h4>
                          <p className="text-sm text-gray-500">
                            Líder: {team.leader.name} | {team.members.length} miembros
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-700">
                            Progreso: {calculateOverallProgress(team.labs_progress)}%
                          </p>
                          <button
                            onClick={() => setTeamCode(team.code)}
                            className="text-xs text-teal-600 hover:text-teal-700"
                          >
                            Usar código: {team.code}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Create Team Tab */}
          {activeTab === 'create' && (
            <div className="space-y-6">
              <div className="text-center">
                <Plus className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Crear Nuevo Equipo</h2>
                <p className="text-gray-600">
                  Serás el líder y podrás invitar a otros miembros
                </p>
              </div>
              
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Equipo
                  </label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Ej: Tech Innovators"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    maxLength={50}
                  />
                </div>
                
                <button
                  onClick={handleCreateTeam}
                  disabled={!teamName.trim() || loading}
                  className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'Creando...' : 'Crear Equipo'}
                </button>
              </div>
            </div>
          )}

          {/* Manage Team Tab */}
          {activeTab === 'manage' && myTeam && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-semibold mb-2">{myTeam.name}</h2>
                <p className="text-gray-600">
                  {myTeam.members.length} miembros | Progreso general: {calculateOverallProgress(myTeam.labs_progress)}%
                </p>
              </div>

              {/* Team Code */}
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Código del equipo:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-lg font-mono font-bold text-gray-900 bg-white px-3 py-1 rounded border">
                    {myTeam.code}
                  </code>
                  <button
                    onClick={copyTeamCode}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors"
                    title="Copiar código"
                  >
                    {copiedCode ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Comparte este código con tus compañeros para que se unan
                </p>
              </div>

              {/* Members */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Miembros del Equipo</h3>
                <div className="space-y-2">
                  {myTeam.members.map((member, index) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">
                            {member.role === 'leader' ? 'Líder del equipo' : 'Miembro'}
                          </p>
                        </div>
                      </div>
                      
                      {member.role === 'leader' && (
                        <Crown className="w-5 h-5 text-yellow-500" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Labs Progress */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Progreso por Laboratorio</h3>
                <div className="space-y-3">
                  {Object.entries(myTeam.labs_progress).map(([labId, progress]) => (
                    <div key={labId} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Lab {labId.replace('lab', '')}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8">{progress}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleStartLabs}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Continuar Laboratorios
                </button>
                
                <button
                  onClick={handleGoHome}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Volver
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSelector;